import { motion, useReducedMotion } from "motion/react";
import { useLocation } from "react-router-dom";
import { useEffect, useState, type ReactNode } from "react";
import { DURATION_QUICK, EASE_OUT } from "@strivelabs/ui-core/lib/ease";

/**
 * Top-level route key so sub-route changes inside a two-pane page do not
 * re-animate the whole pane. `/ui/skills/foo` and `/ui/skills` both key to
 * `/ui/skills`.
 */
function useRouteKey(): string {
  const { pathname } = useLocation();
  const [, first, second] = pathname.split("/");
  return `/${first ?? ""}/${second ?? ""}`;
}

/**
 * Fades routed content in on each top-level navigation.
 *
 * Enter-only, deliberately: `<Outlet />` reads the live router context rather
 * than a snapshot, so an exit animation would render the *incoming* route while
 * pretending to be the outgoing one — the page would flicker out and back in.
 * With no exit to animate, `AnimatePresence` earns nothing here and its
 * exit-tracking is a liability: an interrupted or double-invoked transition can
 * leave the outgoing keyed tree mounted, so pages stack up under one shell. A
 * plain keyed `motion.div` remounts on each top-level route change — React drops
 * the previous tree at once and the new one fades in via `initial`/`animate`.
 * The `mounted` gate reproduces the old `initial={false}`, skipping the fade on
 * first paint; reduced motion drops it to an instant swap.
 */
export function PageTransition({ children }: { children: ReactNode }) {
  const key = useRouteKey();
  const reduce = useReducedMotion();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  if (reduce) {
    return <div className="flex min-h-0 flex-1 flex-col">{children}</div>;
  }

  return (
    <motion.div
      key={key}
      initial={mounted ? { opacity: 0, y: 4 } : false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: DURATION_QUICK, ease: EASE_OUT }}
      className="flex min-h-0 flex-1 flex-col"
    >
      {children}
    </motion.div>
  );
}
